import { useState, useRef, useEffect } from "react";
import { ChevronDown, ChevronUp, ArrowLeft, BookOpen, List, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

const RELATIONSHIP_GUIDE = {
  title: "Relationship Wisdom Coaching — User Guide",
  subtitle: "Your complete guide to the 3A + Harmony Relationship Wisdom Coaching journey",
  sections: [
    {
      id: "r-getting-started",
      title: "Getting Started",
      subsections: [
        {
          id: "r-login",
          title: "Logging In",
          content: [
            "Open the SIWON Harmony app and enter your registered email address and password.",
            "If you are a new user, click 'Register' to create an account. You will receive an OTP to verify your email.",
            "Once logged in, you will land on your personal SIWON Dashboard."
          ]
        },
        {
          id: "r-module-select",
          title: "Selecting the Relationship Wisdom Coaching Module",
          content: [
            "On your dashboard, you will see two module cards at the top — Relationship Wisdom Coaching and Life Wisdom Coaching.",
            "Click on 'Relationship Wisdom Coaching' (marked with ♡) to begin your relationship transformation journey.",
            "The module follows the 3A + Harmony framework: Awareness → Alignment → Awakening → Harmony — four progressive stages of growth."
          ]
        },
        {
          id: "r-framework",
          title: "Understanding the 3A + Harmony Framework",
          content: [
            "Awareness — Discover your current patterns, triggers, and relationship dynamics through diagnostic assessments.",
            "Alignment — Receive personalized daily practices (Sadhana) and action steps based on your diagnostic results.",
            "Awakening — Engage with curated Bhagavad Gita verses and reflective prompts matched to your specific triggers.",
            "Harmony — Integrate your learnings into daily life through reflections, relationship practices, and track your consistency."
          ]
        }
      ]
    },
    {
      id: "r-awareness",
      title: "Tab 1 — Awareness (Diagnostic Tests)",
      subsections: [
        {
          id: "r-12d",
          title: "Step 1: 12D Relationship Profiling",
          content: [
            "This is your primary diagnostic assessment. It evaluates 12 dimensions of your relationship dynamics.",
            "Click the '12D Relationship Profiling' card and answer all questions honestly. There are no right or wrong answers.",
            "Once completed, your scores are calculated across dimensions like Energy Polarity, Guna Balance, Vikara Patterns, Support Network, and Trinity Gap.",
            "You can retake the 12D profiling at any time. Retaking will reset your previous results and regenerate your personalized pipeline."
          ]
        },
        {
          id: "r-tatkalik",
          title: "Step 2: Tatkalik (Contemporary Habits) Quiz",
          content: [
            "This quiz identifies your modern-day habit triggers — daily patterns that affect your relationship quality.",
            "Answer questions about your screen time, communication habits, lifestyle choices, and stress responses.",
            "Results are mapped to specific alignment practices and awakening verses in later tabs."
          ]
        },
        {
          id: "r-issue",
          title: "Step 3: Issue-Specific Diagnosis",
          content: [
            "Select the specific relationship issues you are currently facing from the provided list.",
            "Each selected issue gets mapped to targeted alignment practices, awakening reflections, and harmony commitments.",
            "You can update your selected issues at any time as your situation evolves."
          ]
        },
        {
          id: "r-kosha",
          title: "Step 4: Kosha Deep-Dive Quiz",
          content: [
            "The Kosha Quiz goes deeper into your five energy layers (Annamaya, Pranamaya, Manomaya, Vijnanamaya, Anandamaya).",
            "Answer 25 questions (5 per Kosha) to understand which energy layers need the most attention in your relationship.",
            "Results include a spider/radar chart showing your Kosha balance and personalized recommendations."
          ]
        },
        {
          id: "r-results",
          title: "Viewing Your Diagnostic Results",
          content: [
            "After completing the assessments, scroll down in the Awareness tab to view your results.",
            "Pillar-Trigger Map — Shows your triggers grouped by pillar (Communication, Trust, Intimacy, etc.).",
            "Energy Polarity Chart — Displays your masculine/feminine energy balance and harmony potential.",
            "Guna & Vikara Scores — Shows your Sattva/Rajas/Tamas balance and emotional distortion patterns.",
            "Support Network Analysis — Evaluates your support isolation, mutual ties, and external influence levels.",
            "Kosha Assessment Results — Spider chart showing your five-layer energy balance (click to expand)."
          ]
        }
      ]
    },
    {
      id: "r-alignment",
      title: "Tab 2 — Alignment (Daily Practices)",
      subsections: [
        {
          id: "r-sadhana",
          title: "Daily Sadhana Panel",
          content: [
            "At the top of the Alignment tab, you will see your Daily Sadhana panel with a compliance percentage.",
            "This shows your target practice time (default: 75 minutes) and how well you are meeting it.",
            "Use the 'Regenerate Pipeline' button to refresh your personalized practices if you've retaken any diagnostic test."
          ]
        },
        {
          id: "r-assigned",
          title: "Coach-Assigned Practices",
          content: [
            "Your coach may assign specific practices to you. These appear at the top of the Alignment tab.",
            "Each assigned practice shows the practice name, category, duration, and any special instructions from your coach.",
            "Mark practices as complete using the checkbox next to each item."
          ]
        },
        {
          id: "r-pipeline-align",
          title: "Auto-Generated Alignment Sections",
          content: [
            "Based on your diagnostic results, the system auto-generates personalized alignment practices across multiple dimensions:",
            "Energy Polarity Alignment — Practices to balance your masculine/feminine energy.",
            "Support Network Alignment — Steps to strengthen your relationship support system.",
            "Prayojana Vision Alignment — Practices aligned to your life purpose and relationship vision.",
            "Issue-Specific Alignment — Targeted practices for each issue you selected in your diagnosis.",
            "Tatkalik Alignment — Practices to address your contemporary habit triggers.",
            "Guna Alignment — Practices to improve your Sattva/Rajas/Tamas balance.",
            "Kosha Alignment — Layer-specific practices for your energy body.",
            "Each item has a checkbox — check it off as you complete the practice. Your progress is tracked automatically."
          ]
        }
      ]
    },
    {
      id: "r-awakening",
      title: "Tab 3 — Awakening (Gita Verses & Reflections)",
      subsections: [
        {
          id: "r-verses",
          title: "Pillar-Trigger Awakening",
          content: [
            "This section shows Bhagavad Gita verses that are specifically matched to your personal triggers.",
            "Each trigger from your 12D profiling is paired with relevant verses and their interpretations.",
            "Read and reflect on these verses daily. Use the checkboxes to track which ones you have contemplated."
          ]
        },
        {
          id: "r-pipeline-awaken",
          title: "Dimension-Specific Awakening Prompts",
          content: [
            "Energy Polarity Awakening — Reflective prompts about your energy balance.",
            "Support Network Awakening — Contemplation exercises for deepening your support connections.",
            "Prayojana Vision Awakening — Purpose-oriented reflection prompts.",
            "Guna Awakening — Verses and practices for your dominant Guna pattern.",
            "Kosha Awakening — Layer-specific contemplation verses.",
            "Check off each prompt as you complete your reflection."
          ]
        }
      ]
    },
    {
      id: "r-harmony",
      title: "Tab 4 — Harmony (Integration & Tracking)",
      subsections: [
        {
          id: "r-reflections",
          title: "Pillar-Trigger Harmony (Daily Reflections)",
          content: [
            "This section contains daily reflection items — check them off each day as you complete them.",
            "'Reviewed my triggers and awareness insights today'",
            "'Completed my daily sadhana practices'",
            "'Reflected on today's Gita verses and their meaning'",
            "'Journaled about my emotional patterns and growth'"
          ]
        },
        {
          id: "r-practice-int",
          title: "Relationship Practice Integration",
          content: [
            "These are relationship-specific daily practices you can check off:",
            "'Practiced non-reactivity in a triggering moment'",
            "'Used conscious communication with partner/family'",
            "'Held space for someone without judgment'",
            "'Maintained a healthy boundary with compassion'",
            "'Expressed genuine gratitude to someone close'"
          ]
        },
        {
          id: "r-pipeline-harmony",
          title: "Dimension-Specific Harmony Commitments",
          content: [
            "Similar to Alignment and Awakening, each dimension has Harmony-level commitments:",
            "Energy Polarity Harmony, Support Network Harmony, Prayojana Vision Harmony — long-term integration goals.",
            "Issue-Specific Harmony, Kosha Harmony — deeper commitment items based on your diagnosis.",
            "Check these off as you integrate them into your daily life."
          ]
        }
      ]
    },
    {
      id: "r-tracking",
      title: "Tracking Your Progress",
      subsections: [
        {
          id: "r-compliance",
          title: "Journey Compliance Dashboard",
          content: [
            "At the top of your module view, a circular progress indicator shows your overall compliance percentage.",
            "This is calculated from your checked items across all four tabs (Awareness completion, Alignment practices, Awakening reflections, Harmony integrations).",
            "Aim for consistent daily progress rather than trying to complete everything at once."
          ]
        },
        {
          id: "r-weekly",
          title: "Weekly Sadhana Tracker",
          content: [
            "Below the compliance dashboard, your Weekly Progress Summary shows day-by-day activity.",
            "Each day shows how many items you checked off. Active days are highlighted.",
            "The streak counter shows your consecutive days of practice. Keep the streak alive for best results!"
          ]
        },
        {
          id: "r-pipeline-check",
          title: "Pipeline Checkboxes & Progress",
          content: [
            "Every practice, verse, and reflection across all tabs has an interactive checkbox.",
            "Checked items are saved instantly to the server. Your progress is preserved across sessions.",
            "The count in each section header (e.g., '3 practices') shows how many items are available.",
            "Your overall compliance percentage updates in real-time as you check items off."
          ]
        }
      ]
    },
    {
      id: "r-subscription",
      title: "Subscription & Pricing",
      subsections: [
        {
          id: "r-plans",
          title: "Choosing a Subscription Plan",
          content: [
            "Navigate to the Pricing page from the dashboard or via the 'Subscribe' button.",
            "Choose your billing period: Monthly, Quarterly, or Annual. Quarterly and Annual plans offer savings over monthly billing.",
            "Select your currency (INR for domestic, USD for international users). Your region is auto-detected from your country at registration.",
            "Choose between Relationship Module, Life Module, or the Complete Bundle (both modules at a discounted rate)."
          ]
        },
        {
          id: "r-promos",
          title: "Promo Offers & Coupons",
          content: [
            "Active promotional offers appear as a banner at the top of the Pricing page with a countdown timer.",
            "Promo coupons are auto-applied — you'll see a green 'Applied' badge. Discounted prices show on plan cards instantly.",
            "If you have a different coupon code, click 'Have a coupon or referral code?' to enter it manually.",
            "You can remove an applied coupon by clicking the '×' next to it."
          ]
        },
        {
          id: "r-terms",
          title: "Terms of Service & Payment",
          content: [
            "Before subscribing, you must read and accept the Terms of Service by checking the consent checkbox.",
            "Click the 'Terms of Service' link to view the full legal document in a new tab.",
            "The checkbox must be checked before any payment can proceed — this is a legal requirement.",
            "Your acceptance is recorded with a timestamp and linked to your order for audit purposes."
          ]
        },
        {
          id: "r-billing",
          title: "GST & Billing Details",
          content: [
            "Optionally, you can add your organization name and GST number for business invoices.",
            "Click 'Add GST / Billing Details' to expand the form. These details appear on your invoice.",
            "Domestic clients are charged 18% GST on all services. International clients may have NIL GST under LUT provisions."
          ]
        }
      ]
    },
    {
      id: "r-referral",
      title: "Referral Program — Share & Earn",
      subsections: [
        {
          id: "r-ref-code",
          title: "Your Referral Code",
          content: [
            "Once subscribed, you receive a unique referral code (e.g., REF-VINOD-7X3K) displayed on your dashboard.",
            "The 'Share & Earn' card appears on your dashboard with your code, copy buttons, and referral stats.",
            "You earn a reward coupon when someone subscribes using your referral code."
          ]
        },
        {
          id: "r-ref-share",
          title: "Sharing Your Referral",
          content: [
            "Use the quick share buttons on your referral card: WhatsApp, X/Twitter, or Email — with a pre-filled message.",
            "You can also copy your referral code or the full referral link to share anywhere.",
            "When someone visits the link, your referral code is auto-filled on the pricing page."
          ]
        },
        {
          id: "r-ref-rewards",
          title: "Rewards",
          content: [
            "When someone subscribes using your code, both of you receive reward coupons.",
            "Reward coupons appear in the 'Your reward coupons' section of your referral card.",
            "Use reward coupons at checkout — they work just like regular coupon codes."
          ]
        }
      ]
    },
    {
      id: "r-reports",
      title: "3A Reports & Coaching Calls",
      subsections: [
        {
          id: "r-order-report",
          title: "Ordering a 3A Framework Report",
          content: [
            "From your dashboard, use the 'Order 3A Report' action card to request a comprehensive PDF analysis.",
            "The report is generated from your diagnostic data (12D Profiling, Kosha, Vikara, Guna scores).",
            "Complete payment through the in-app flow. You'll receive the report via email with an invoice attached."
          ]
        },
        {
          id: "r-book-call",
          title: "Booking a Coaching Call",
          content: [
            "Use the 'Book Coaching Call' action card on your dashboard to schedule a one-on-one session.",
            "After payment, you'll be redirected to a WhatsApp group to coordinate your call timing.",
            "An invoice is automatically generated and emailed to you."
          ]
        },
        {
          id: "r-invoices",
          title: "Invoices",
          content: [
            "All paid orders generate GST-compliant invoices automatically.",
            "View and download your invoices from the invoice section on your dashboard.",
            "Invoices include all tax details, payment reference, and are valid for accounting purposes."
          ]
        }
      ]
    },
    {
      id: "r-account",
      title: "Your Account",
      subsections: [
        {
          id: "r-registration",
          title: "Registration",
          content: [
            "Register with your name, email, mobile number (with country code selector), password, and country.",
            "Select your country code from the dropdown with country flags (e.g., +91 India, +1 US, +44 UK).",
            "Verify your email with a one-time OTP sent to your inbox. Check spam if you don't see it.",
            "Your country selection determines your region (domestic/international) which affects pricing and currency."
          ]
        },
        {
          id: "r-login-methods",
          title: "Login Methods",
          content: [
            "Password Login — Enter your email and password.",
            "Email OTP Login — Request a one-time password sent to your email. No password needed.",
            "Forgot Password — Click 'Forgot Password?' to receive a reset OTP via email."
          ]
        },
        {
          id: "r-notifications",
          title: "Notifications",
          content: [
            "Enable push notifications when prompted to receive updates about your coaching journey.",
            "Notifications include daily sadhana reminders, coach messages, and subscription updates.",
            "You can manage notification preferences from your browser settings."
          ]
        }
      ]
    }
  ]
};

const LIFE_GUIDE = {
  title: "Life Wisdom Coaching — User Guide",
  subtitle: "Your complete guide to the 3A + Harmony Life Wisdom Coaching journey",
  sections: [
    {
      id: "l-getting-started",
      title: "Getting Started",
      subsections: [
        {
          id: "l-login",
          title: "Logging In",
          content: [
            "Open the SIWON Harmony app and enter your registered email address and password.",
            "If you are a new user, click 'Register' to create an account. You will receive an OTP to verify your email.",
            "Once logged in, you will land on your personal SIWON Dashboard."
          ]
        },
        {
          id: "l-module-select",
          title: "Selecting the Life Wisdom Coaching Module",
          content: [
            "On your dashboard, you will see two module cards at the top — Relationship Wisdom Coaching and Life Wisdom Coaching.",
            "Click on 'Life Wisdom Coaching' (marked with ☸) to begin your personal life transformation journey.",
            "The module follows the 3A + Harmony framework: Awareness → Alignment → Awakening → Harmony — four progressive stages of growth."
          ]
        },
        {
          id: "l-framework",
          title: "Understanding the 3A + Harmony Framework",
          content: [
            "Awareness — Discover your life patterns, strengths, and growth areas through the 9-module Life Diagnostic Engine.",
            "Alignment — Receive personalized daily practices (Sadhana) and action steps based on your diagnostic results.",
            "Awakening — Engage with curated Bhagavad Gita verses and reflective prompts matched to your specific life themes.",
            "Harmony — Integrate your learnings into daily life through reflections, life practices, and track your consistency."
          ]
        }
      ]
    },
    {
      id: "l-awareness",
      title: "Tab 1 — Awareness (Diagnostic Tests)",
      subsections: [
        {
          id: "l-diagnostic",
          title: "Step 1: 9-Module Life Diagnostic Engine",
          content: [
            "This is your primary life assessment. It covers 9 progressive modules, each evaluating a key life dimension.",
            "Modules are unlocked sequentially — complete one module to unlock the next.",
            "Answer all questions in each module honestly. Your scores build progressively, showing your growth areas.",
            "After completing each module, you can view your results immediately. Full results are available after all 9 modules are done.",
            "You can retake the Life Diagnostic at any time. Retaking resets all module scores."
          ]
        },
        {
          id: "l-kosha",
          title: "Step 2: Kosha Deep-Dive Quiz (Life)",
          content: [
            "The Life Kosha Quiz evaluates your five energy layers (Annamaya, Pranamaya, Manomaya, Vijnanamaya, Anandamaya) in the context of your overall life wellness.",
            "Answer 25 questions (5 per Kosha) to understand which energy layers need the most attention in your life.",
            "Results include a spider/radar chart showing your Kosha balance and personalized recommendations."
          ]
        },
        {
          id: "l-issue",
          title: "Step 3: Issue-Specific Diagnosis (Life)",
          content: [
            "Select the specific life challenges you are currently facing from the provided list.",
            "Each selected issue gets mapped to targeted alignment practices, awakening reflections, and harmony commitments.",
            "You can update your selected issues at any time as your life situation evolves."
          ]
        },
        {
          id: "l-results",
          title: "Viewing Your Diagnostic Results",
          content: [
            "After completing the assessments, view your progressive results in the Awareness tab.",
            "Life Diagnostic scores are shown per module with visual indicators (completed, in-progress, locked).",
            "Kosha Assessment Results — Spider chart showing your five-layer energy balance.",
            "Issue Diagnosis Results — Summary of your selected life challenges and their mapped solutions."
          ]
        }
      ]
    },
    {
      id: "l-alignment",
      title: "Tab 2 — Alignment (Daily Practices)",
      subsections: [
        {
          id: "l-sadhana",
          title: "Daily Sadhana Panel",
          content: [
            "At the top of the Alignment tab, you will see your Daily Sadhana panel with a compliance percentage.",
            "This shows your target practice time and how well you are meeting it.",
            "Use the 'Regenerate Pipeline' button to refresh your personalized practices if you've retaken any diagnostic test."
          ]
        },
        {
          id: "l-diag-align",
          title: "Life Diagnostic Alignment",
          content: [
            "Based on your 9-module Life Diagnostic results, personalized alignment practices are generated.",
            "Each completed diagnostic module contributes specific practices to this section.",
            "Follow these practices daily to address the areas identified in your assessment."
          ]
        },
        {
          id: "l-pipeline-align",
          title: "Dimension-Specific Alignment Sections",
          content: [
            "Kosha Alignment — Layer-specific practices for your energy body based on your Kosha quiz results.",
            "Energy Polarity Alignment — Practices to balance your energy patterns.",
            "Vikara Alignment — Practices targeting emotional distortion patterns.",
            "Tatkalik Alignment — Practices to address your modern-day habit triggers.",
            "Issue-Specific Alignment — Targeted practices for each life issue you selected.",
            "Each item has a checkbox — check it off as you complete the practice. Your progress is tracked automatically."
          ]
        }
      ]
    },
    {
      id: "l-awakening",
      title: "Tab 3 — Awakening (Gita Verses & Reflections)",
      subsections: [
        {
          id: "l-verses",
          title: "Pillar-Trigger Awakening",
          content: [
            "This section shows Bhagavad Gita verses specifically matched to your life themes and growth areas.",
            "Verses are grouped by the pillars identified in your diagnostic results.",
            "Read and reflect on these verses daily. Use the checkboxes to track which ones you have contemplated."
          ]
        },
        {
          id: "l-pipeline-awaken",
          title: "Dimension-Specific Awakening Prompts",
          content: [
            "Energy Polarity Awakening — Reflective prompts about your energy balance.",
            "Vikara Awakening — Contemplation exercises for your emotional patterns.",
            "Guna Awakening — Verses aligned with your Sattva/Rajas/Tamas balance.",
            "Kosha Awakening — Layer-specific contemplation verses.",
            "Check off each prompt as you complete your reflection."
          ]
        }
      ]
    },
    {
      id: "l-harmony",
      title: "Tab 4 — Harmony (Integration & Tracking)",
      subsections: [
        {
          id: "l-reflections",
          title: "Pillar-Trigger Harmony (Daily Reflections)",
          content: [
            "This section contains daily reflection items — check them off each day as you complete them.",
            "'Reviewed my triggers and awareness insights today'",
            "'Completed my daily sadhana practices'",
            "'Reflected on today's Gita verses and their meaning'",
            "'Journaled about my emotional patterns and growth'"
          ]
        },
        {
          id: "l-practice-int",
          title: "Life Practice Integration",
          content: [
            "These are life-specific daily practices you can check off:",
            "Practices cover areas like mindfulness, discipline, self-awareness, boundary-setting, and gratitude.",
            "The goal is to integrate your diagnostic insights into your everyday actions."
          ]
        },
        {
          id: "l-pipeline-harmony",
          title: "Dimension-Specific Harmony Commitments",
          content: [
            "Each dimension (Energy Polarity, Vikara, Guna, Kosha) has Harmony-level commitments.",
            "These are long-term integration goals based on your life diagnostic results.",
            "Issue-Specific Harmony — Deeper commitments tied to your selected life challenges.",
            "Check these off as you integrate them into your daily life."
          ]
        }
      ]
    },
    {
      id: "l-tracking",
      title: "Tracking Your Progress",
      subsections: [
        {
          id: "l-compliance",
          title: "Journey Compliance Dashboard",
          content: [
            "At the top of your module view, a circular progress indicator shows your overall compliance percentage.",
            "This is calculated from your checked items across all four tabs (Awareness completion, Alignment practices, Awakening reflections, Harmony integrations).",
            "Aim for consistent daily progress rather than trying to complete everything at once."
          ]
        },
        {
          id: "l-weekly",
          title: "Weekly Sadhana Tracker",
          content: [
            "Below the compliance dashboard, your Weekly Progress Summary shows day-by-day activity.",
            "Each day shows how many items you checked off. Active days are highlighted.",
            "The streak counter shows your consecutive days of practice. Keep the streak alive for best results!"
          ]
        },
        {
          id: "l-pipeline-check",
          title: "Pipeline Checkboxes & Progress",
          content: [
            "Every practice, verse, and reflection across all tabs has an interactive checkbox.",
            "Checked items are saved instantly to the server. Your progress is preserved across sessions.",
            "The count in each section header (e.g., '3 practices') shows how many items are available.",
            "Your overall compliance percentage updates in real-time as you check items off."
          ]
        }
      ]
    },
    {
      id: "l-subscription",
      title: "Subscription & Pricing",
      subsections: [
        {
          id: "l-plans",
          title: "Choosing a Subscription Plan",
          content: [
            "Navigate to the Pricing page from the dashboard or via the 'Subscribe' button.",
            "Choose your billing period: Monthly, Quarterly, or Annual. Quarterly and Annual plans offer savings over monthly billing.",
            "Select your currency (INR for domestic, USD for international users). Your region is auto-detected from your country at registration.",
            "Choose between Relationship Module, Life Module, or the Complete Bundle (both modules at a discounted rate)."
          ]
        },
        {
          id: "l-promos",
          title: "Promo Offers & Coupons",
          content: [
            "Active promotional offers appear as a banner at the top of the Pricing page with a countdown timer.",
            "Promo coupons are auto-applied — you'll see a green 'Applied' badge. Discounted prices show on plan cards instantly.",
            "If you have a different coupon code, click 'Have a coupon or referral code?' to enter it manually.",
            "You can remove an applied coupon by clicking the '×' next to it."
          ]
        },
        {
          id: "l-terms",
          title: "Terms of Service & Payment",
          content: [
            "Before subscribing, you must read and accept the Terms of Service by checking the consent checkbox.",
            "Click the 'Terms of Service' link to view the full legal document in a new tab.",
            "The checkbox must be checked before any payment can proceed — this is a legal requirement.",
            "Your acceptance is recorded with a timestamp and linked to your order for audit purposes."
          ]
        },
        {
          id: "l-billing",
          title: "GST & Billing Details",
          content: [
            "Optionally, you can add your organization name and GST number for business invoices.",
            "Click 'Add GST / Billing Details' to expand the form. These details appear on your invoice.",
            "Domestic clients are charged 18% GST on all services. International clients may have NIL GST under LUT provisions."
          ]
        }
      ]
    },
    {
      id: "l-referral",
      title: "Referral Program — Share & Earn",
      subsections: [
        {
          id: "l-ref-code",
          title: "Your Referral Code",
          content: [
            "Once subscribed, you receive a unique referral code (e.g., REF-VINOD-7X3K) displayed on your dashboard.",
            "The 'Share & Earn' card appears on your dashboard with your code, copy buttons, and referral stats.",
            "You earn a reward coupon when someone subscribes using your referral code."
          ]
        },
        {
          id: "l-ref-share",
          title: "Sharing Your Referral",
          content: [
            "Use the quick share buttons on your referral card: WhatsApp, X/Twitter, or Email — with a pre-filled message.",
            "You can also copy your referral code or the full referral link to share anywhere.",
            "When someone visits the link, your referral code is auto-filled on the pricing page."
          ]
        },
        {
          id: "l-ref-rewards",
          title: "Rewards",
          content: [
            "When someone subscribes using your code, both of you receive reward coupons.",
            "Reward coupons appear in the 'Your reward coupons' section of your referral card.",
            "Use reward coupons at checkout — they work just like regular coupon codes."
          ]
        }
      ]
    },
    {
      id: "l-reports",
      title: "3A Reports & Coaching Calls",
      subsections: [
        {
          id: "l-order-report",
          title: "Ordering a 3A Framework Report",
          content: [
            "From your dashboard, use the 'Order 3A Report' action card to request a comprehensive PDF analysis.",
            "The report covers your Life Diagnostic results, Kosha scores, and personalized recommendations.",
            "Complete payment through the in-app flow. You'll receive the report via email with an invoice attached."
          ]
        },
        {
          id: "l-book-call",
          title: "Booking a Coaching Call",
          content: [
            "Use the 'Book Coaching Call' action card on your dashboard to schedule a one-on-one session.",
            "After payment, you'll be redirected to a WhatsApp group to coordinate your call timing.",
            "An invoice is automatically generated and emailed to you."
          ]
        },
        {
          id: "l-invoices",
          title: "Invoices",
          content: [
            "All paid orders generate GST-compliant invoices automatically.",
            "View and download your invoices from the invoice section on your dashboard.",
            "Invoices include all tax details, payment reference, and are valid for accounting purposes."
          ]
        }
      ]
    },
    {
      id: "l-account",
      title: "Your Account",
      subsections: [
        {
          id: "l-registration",
          title: "Registration",
          content: [
            "Register with your name, email, mobile number (with country code selector), password, and country.",
            "Select your country code from the dropdown with country flags (e.g., +91 India, +1 US, +44 UK).",
            "Verify your email with a one-time OTP sent to your inbox. Check spam if you don't see it.",
            "Your country selection determines your region (domestic/international) which affects pricing and currency."
          ]
        },
        {
          id: "l-login-methods",
          title: "Login Methods",
          content: [
            "Password Login — Enter your email and password.",
            "Email OTP Login — Request a one-time password sent to your email. No password needed.",
            "Forgot Password — Click 'Forgot Password?' to receive a reset OTP via email."
          ]
        },
        {
          id: "l-notifications",
          title: "Notifications",
          content: [
            "Enable push notifications when prompted to receive updates about your coaching journey.",
            "Notifications include daily sadhana reminders, coach messages, and subscription updates.",
            "You can manage notification preferences from your browser settings."
          ]
        }
      ]
    }
  ]
};

const SectionContent = ({ section, expandedSections, toggleSection }) => {
  const isOpen = expandedSections.has(section.id);
  return (
    <div className="border border-divine-gold/10 rounded-xl overflow-hidden" data-testid={`guide-section-${section.id}`}>
      <button
        onClick={() => toggleSection(section.id)}
        className="w-full flex items-center justify-between p-4 bg-cosmic-light/5 hover:bg-cosmic-light/10 transition-colors text-left"
        data-testid={`guide-toggle-${section.id}`}
      >
        <h2 className="font-heading text-lg text-divine-gold">{section.title}</h2>
        {isOpen ? <ChevronUp className="w-5 h-5 text-divine-gold/60" /> : <ChevronDown className="w-5 h-5 text-divine-gold/60" />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="p-4 space-y-5">
              {section.subsections.map((sub) => (
                <div key={sub.id} id={sub.id} className="scroll-mt-24" data-testid={`guide-sub-${sub.id}`}>
                  <h3 className="font-heading text-base text-chakra-crown mb-2">{sub.title}</h3>
                  <ul className="space-y-2">
                    {sub.content.map((line, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-chakra-crown/70 leading-relaxed">
                        <ChevronRight className="w-4 h-4 text-divine-gold/40 mt-0.5 shrink-0" />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const GuideView = ({ guide, onBack }) => {
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [showIndex, setShowIndex] = useState(true);
  const contentRef = useRef(null);

  const toggleSection = (id) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpandedSections(new Set(guide.sections.map(s => s.id)));
  const collapseAll = () => setExpandedSections(new Set());

  const scrollToSection = (sectionId) => {
    setExpandedSections(prev => new Set([...prev, sectionId]));
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="guide-section-${sectionId}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const scrollToSub = (sectionId, subId) => {
    setExpandedSections(prev => new Set([...prev, sectionId]));
    setTimeout(() => {
      const el = document.getElementById(subId);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-divine-gold hover:bg-divine-gold/10" data-testid="guide-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowIndex(!showIndex)} className="border-divine-gold/20 text-divine-gold/70 hover:bg-divine-gold/10 text-xs" data-testid="guide-toggle-index">
            <List className="w-3 h-3 mr-1" /> {showIndex ? "Hide" : "Show"} Index
          </Button>
          <Button variant="outline" size="sm" onClick={expandAll} className="border-divine-gold/20 text-divine-gold/70 hover:bg-divine-gold/10 text-xs" data-testid="guide-expand-all">
            Expand All
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll} className="border-divine-gold/20 text-divine-gold/70 hover:bg-divine-gold/10 text-xs" data-testid="guide-collapse-all">
            Collapse All
          </Button>
        </div>
      </div>

      {/* Title */}
      <div className="sacred-card text-center py-6">
        <BookOpen className="w-8 h-8 text-divine-gold mx-auto mb-3" />
        <h1 className="font-heading text-2xl md:text-3xl text-divine-gold">{guide.title}</h1>
        <p className="text-chakra-crown/50 mt-2 text-sm">{guide.subtitle}</p>
      </div>

      <div className={`${showIndex ? 'grid grid-cols-1 lg:grid-cols-4 gap-6' : ''}`}>
        {/* Index Sidebar */}
        <AnimatePresence>
          {showIndex && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="lg:col-span-1"
            >
              <div className="sacred-card sticky top-24 space-y-3" data-testid="guide-index">
                <h3 className="font-heading text-sm text-divine-gold mb-3 uppercase tracking-wider">Table of Contents</h3>
                {guide.sections.map((section, si) => (
                  <div key={section.id}>
                    <button
                      onClick={() => scrollToSection(section.id)}
                      className="text-sm text-chakra-crown hover:text-divine-gold transition-colors text-left w-full font-heading"
                      data-testid={`index-${section.id}`}
                    >
                      {si + 1}. {section.title}
                    </button>
                    <div className="ml-4 mt-1 space-y-0.5">
                      {section.subsections.map((sub, subi) => (
                        <button
                          key={sub.id}
                          onClick={() => scrollToSub(section.id, sub.id)}
                          className="block text-xs text-chakra-crown/50 hover:text-divine-gold/80 transition-colors text-left w-full truncate"
                        >
                          {si + 1}.{subi + 1} {sub.title}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Content */}
        <div ref={contentRef} className={showIndex ? "lg:col-span-3 space-y-4" : "space-y-4"}>
          {guide.sections.map((section) => (
            <SectionContent
              key={section.id}
              section={section}
              expandedSections={expandedSections}
              toggleSection={toggleSection}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default function UserGuide({ activeModule, onClose, moduleAccess }) {
  const [selectedGuide, setSelectedGuide] = useState(activeModule || "relationship");
  const access = moduleAccess || ["relationship", "life"];

  useEffect(() => {
    setSelectedGuide(activeModule);
  }, [activeModule]);

  const guideModules = [
    { id: "relationship", label: "Relationship Wisdom Coaching Guide", icon: "♡" },
    { id: "life", label: "Life Wisdom Coaching Guide", icon: "☸" },
  ].filter(m => access.includes(m.id));

  return (
    <div className="space-y-6">
      {/* Module Guide Selector — only show subscribed modules */}
      {guideModules.length > 1 && (
      <div className="flex gap-3 mb-2" data-testid="guide-module-selector">
        {guideModules.map(m => (
          <button
            key={m.id}
            onClick={() => setSelectedGuide(m.id)}
            className={`flex-1 p-3 rounded-xl border transition-all text-center ${
              selectedGuide === m.id
                ? "bg-divine-gold/10 border-divine-gold text-divine-gold"
                : "bg-cosmic-light/5 border-divine-gold/10 text-chakra-crown/60 hover:border-divine-gold/30"
            }`}
            data-testid={`guide-select-${m.id}`}
          >
            <span className="text-xl block mb-1">{m.icon}</span>
            <span className="font-heading text-sm">{m.label}</span>
          </button>
        ))}
      </div>
      )}

      {/* Selected Guide */}
      {selectedGuide === "relationship" && access.includes("relationship") && (
        <GuideView guide={RELATIONSHIP_GUIDE} onBack={onClose} />
      )}
      {selectedGuide === "life" && access.includes("life") && (
        <GuideView guide={LIFE_GUIDE} onBack={onClose} />
      )}
    </div>
  );
}
